import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/searchable-select';
import {
  useCreateInsured,
  useUpdateInsured,
  useSalesAgents,
  useGetInsuredByDot,
  useGetInsuredByName,
  useGetInsuredByEmail,
  useGetInsuredByPhone,
} from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { formatPhoneDisplay, unformatPhone } from '@/lib/formatters';
import { RETENTION_AGENT_TEAM } from '@shared/retentionAgents';

const ENTITY_TYPES = [
  'Sole Prop',
  'LLC',
  'Corporation',
  'Partnership',
  'Other',
] as const;

const RETENTION_AGENT_NONE = '__none__';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

interface FormErrors {
  name?: string;
  companyOfficer?: string;
  email?: string;
  telephone?: string;
}

// CSR-1823 / CSR-1831 — which merge key the existing insured was matched on.
// `dot` is an identity match: the Data Center would update that same record, so a
// second one can't be created. `email`/`phone`/`name` are contact/soft matches:
// creating a separate insured is allowed (one person may own several companies),
// so those offer a "Create new" that forces DC to insert a distinct row
// (skipContactDedup).
type DedupMatch = { id: string; name: string | null; kind: 'dot' | 'email' | 'phone' | 'name' };

export function AddInsuredDialog({ open, onOpenChange, onCreated }: Props) {
  const [name, setName] = useState('');
  const [dbaName, setDbaName] = useState('');
  const [businessOrg, setBusinessOrg] = useState('');
  const [dotNumber, setDotNumber] = useState('');
  const [companyOfficer, setCompanyOfficer] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [salesAgent, setSalesAgent] = useState('');
  const [retentionAgent, setRetentionAgent] = useState('');
  const [dedupMatch, setDedupMatch] = useState<DedupMatch | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const create = useCreateInsured();
  const update = useUpdateInsured();
  const { toast } = useToast();
  const { data: salesAgents } = useSalesAgents();
  const salesAgentOptions = useMemo(
    () => (salesAgents ?? []).map((name) => ({ value: name, label: name })),
    [salesAgents],
  );

  const byDot = useGetInsuredByDot(dotNumber.trim() || null);
  const byName = useGetInsuredByName(name.trim() || null);
  const byEmail = useGetInsuredByEmail(email.trim() || null);
  const byPhone = useGetInsuredByPhone(telephone.trim() || null);

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!name.trim()) next.name = 'Company / Insured Name is required.';
    if (!companyOfficer.trim()) next.companyOfficer = 'Contact Name is required.';
    if (!email.trim()) {
      next.email = 'Email is required.';
    } else if (!EMAIL_RE.test(email.trim())) {
      next.email = 'Enter a valid email address.';
    }
    if (!telephone.trim()) {
      next.telephone = 'Phone is required.';
    } else if (telephone.length !== 10) {
      next.telephone = 'Enter a valid 10-digit phone number.';
    }
    return next;
  };

  const enteredFields = () => ({
    name: name.trim() || null,
    dbaName: dbaName.trim() || null,
    businessOrg: businessOrg.trim() || null,
    dotNumber: dotNumber.trim() || null,
    companyOfficer: companyOfficer.trim() || null,
    email: email.trim() || null,
    telephone: telephone.trim() || null,
    salesAgent: salesAgent.trim() || null,
    retentionAgent: retentionAgent.trim() || null,
  });

  const openInsured = (id: string) => {
    onOpenChange(false);
    window.location.assign(`/insureds/${id}`);
  };

  // CSR-1823 — `skipContactDedup` is forwarded for a deliberate "Create new" on
  // ANY non-DOT match (email, or the soft name warning), so DC INSERTs the new
  // company as its own row instead of silently merging it onto an insured that
  // shares a contact value. The user has explicitly chosen a distinct record, so
  // bypassing DC's email/phone rungs is the intended, safe behavior — it prevents
  // the CSR-1778 silent merge (e.g. a phone that happens to match an unrelated
  // insured). Merging is what the separate "Update existing" action is for. A
  // plain no-match create omits the flag and follows DC's normal cascade.
  const performCreate = (opts?: { skipContactDedup?: boolean }) => {
    create.mutate(
      { ...enteredFields(), ...(opts?.skipContactDedup ? { skipContactDedup: true } : {}) },
      {
        onSuccess: (created) => {
          toast({ title: 'Insured created' });
          onOpenChange(false);
          if (onCreated && (created as { id?: string })?.id) {
            onCreated((created as { id: string }).id);
          }
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'Failed to create insured';
          toast({
            title: 'Failed to create insured',
            description: message,
            variant: 'destructive',
          });
        },
      },
    );
  };

  // CSR-1823 — "Update existing" applies the entered fields to the matched insured
  // (rather than creating a record), then opens it.
  const updateExisting = () => {
    if (!dedupMatch) return;
    const { id } = dedupMatch;
    update.mutate(
      { id, data: enteredFields() },
      {
        onSuccess: () => {
          toast({ title: 'Insured updated' });
          openInsured(id);
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'Failed to update insured';
          toast({
            title: 'Failed to update insured',
            description: message,
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleSubmit = async () => {
    if (create.isPending || update.isPending) return;
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    // Detect against the Data Center's merge keys before saving. DOT is an
    // identity match (DC would update that same record); email and phone are
    // real merge keys; name is a soft duplicate warning (not a DC merge key).
    // Order mirrors DC's cascade: DOT → email → phone, then name last (CSR-1831
    // completed the phone slice once DC gained a phone filter — CSR-1826).
    let match: DedupMatch | null = null;
    if (dotNumber.trim()) {
      const res = await byDot.refetch();
      if (res.data && (res.data as any).id) {
        match = { id: (res.data as any).id, name: (res.data as any).name ?? null, kind: 'dot' };
      }
    }
    if (!match && email.trim()) {
      const res = await byEmail.refetch();
      if (res.data && (res.data as any).id) {
        match = { id: (res.data as any).id, name: (res.data as any).name ?? null, kind: 'email' };
      }
    }
    if (!match && telephone.trim()) {
      const res = await byPhone.refetch();
      if (res.data && (res.data as any).id) {
        match = { id: (res.data as any).id, name: (res.data as any).name ?? null, kind: 'phone' };
      }
    }
    if (!match && name.trim()) {
      const res = await byName.refetch();
      if (res.data && (res.data as any).id) {
        match = { id: (res.data as any).id, name: (res.data as any).name ?? null, kind: 'name' };
      }
    }
    if (match) {
      setDedupMatch(match);
      return;
    }
    performCreate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add insured</DialogTitle>
          <DialogDescription>
            Create a new insured. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>
        {dedupMatch && (
          <div
            data-testid="add-insured-dedup-warning"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
          >
            <p>
              An insured with that{' '}
              {dedupMatch.kind === 'dot'
                ? 'DOT'
                : dedupMatch.kind === 'email'
                  ? 'email'
                  : dedupMatch.kind === 'phone'
                    ? 'phone'
                    : 'name'}{' '}
              already exists: <strong>{dedupMatch.name ?? '(no name)'}</strong>.
              {dedupMatch.kind === 'dot'
                ? ' A matching DOT updates that record — you can’t create a second one.'
                : ' You can update it, or create a separate new insured.'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="cursor-pointer"
                data-testid="add-insured-open-existing"
                disabled={create.isPending || update.isPending}
                onClick={() => openInsured(dedupMatch.id)}
              >
                Open existing
              </Button>
              <Button
                size="sm"
                className="cursor-pointer"
                data-testid="add-insured-update-existing"
                disabled={create.isPending || update.isPending}
                onClick={updateExisting}
              >
                {dedupMatch.kind === 'dot' ? 'Update existing one' : 'Update existing'}
              </Button>
              {dedupMatch.kind !== 'dot' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="cursor-pointer"
                  data-testid="add-insured-create-new"
                  disabled={create.isPending || update.isPending}
                  onClick={() => {
                    setDedupMatch(null);
                    performCreate({ skipContactDedup: true });
                  }}
                >
                  Create new
                </Button>
              )}
            </div>
          </div>
        )}
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="add-insured-name">Company / Insured Name *</Label>
            <Input
              id="add-insured-name"
              data-testid="add-insured-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDedupMatch(null);
              }}
              placeholder="e.g. Acme Trucking LLC"
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p data-testid="add-insured-name-error" className="text-xs text-destructive">
                {errors.name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-insured-dba">DBA Name</Label>
            <Input
              id="add-insured-dba"
              data-testid="add-insured-dba"
              value={dbaName}
              onChange={(e) => setDbaName(e.target.value)}
              placeholder="e.g. Acme Express"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-insured-entity">Entity Type</Label>
              <Select
                value={businessOrg || undefined}
                onValueChange={(v) => setBusinessOrg(v)}
              >
                <SelectTrigger id="add-insured-entity" data-testid="add-insured-entity">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-insured-dot">DOT Number</Label>
              <Input
                id="add-insured-dot"
                data-testid="add-insured-dot"
                value={dotNumber}
                onChange={(e) => {
                  setDotNumber(e.target.value);
                  setDedupMatch(null);
                }}
                placeholder="e.g. 1234567"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Contact Information</h3>
            <div className="space-y-2">
              <Label htmlFor="add-insured-contact">Contact Name *</Label>
              <Input
                id="add-insured-contact"
                data-testid="add-insured-contact"
                value={companyOfficer}
                onChange={(e) => setCompanyOfficer(e.target.value)}
                placeholder="e.g. John Smith"
                className={errors.companyOfficer ? 'border-destructive' : ''}
              />
              {errors.companyOfficer && (
                <p data-testid="add-insured-contact-error" className="text-xs text-destructive">
                  {errors.companyOfficer}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-insured-email">Email *</Label>
                <Input
                  id="add-insured-email"
                  data-testid="add-insured-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setDedupMatch(null);
                  }}
                  placeholder="name@company.com"
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p data-testid="add-insured-email-error" className="text-xs text-destructive">
                    {errors.email}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-insured-tel">Phone *</Label>
                <Input
                  id="add-insured-tel"
                  data-testid="add-insured-tel"
                  value={formatPhoneDisplay(telephone)}
                  onChange={(e) => {
                    setTelephone(unformatPhone(e.target.value));
                    setDedupMatch(null);
                  }}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  className={errors.telephone ? 'border-destructive' : ''}
                />
                {errors.telephone && (
                  <p data-testid="add-insured-tel-error" className="text-xs text-destructive">
                    {errors.telephone}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Assignment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-insured-sales-agent">Sales Agent</Label>
                <SearchableSelect
                  value={salesAgent}
                  onChange={setSalesAgent}
                  options={salesAgentOptions}
                  placeholder="Select a sales agent…"
                />
                {/* Test-only handle: drives state without going through Radix
                    popover (hard to drive in happy-dom). */}
                <input
                  type="text"
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                  data-testid="add-insured-sales-agent"
                  value={salesAgent}
                  onChange={(e) => setSalesAgent(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-insured-retention-agent">Retention Agent</Label>
                <Select
                  value={retentionAgent || RETENTION_AGENT_NONE}
                  onValueChange={(v) =>
                    setRetentionAgent(v === RETENTION_AGENT_NONE ? '' : v)
                  }
                >
                  <SelectTrigger
                    id="add-insured-retention-agent"
                    data-testid="add-insured-retention-agent"
                  >
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={RETENTION_AGENT_NONE}>None</SelectItem>
                    {RETENTION_AGENT_TEAM.map((member) => (
                      <SelectItem key={member} value={member}>
                        {member}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button
            data-testid="add-insured-submit"
            disabled={create.isPending || update.isPending || dedupMatch !== null}
            onClick={handleSubmit}
            className="cursor-pointer"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
